import React, { useMemo } from 'react';

import type {
  ReportsFilterOptionGroup,
  ReportsFilterSelection,
} from 'components/Reports/hooks/useReportsFilterOptions';

import {
  ComboboxGroupedField,
  type ComboboxGroupedFieldGroup,
} from 'components/DesignSystems/ComboboxGroupedField/ComboboxGroupedField';

type ReportsFilterBarProps = {
  groupedOptions: ReportsFilterOptionGroup[];
  selectedFilter: ReportsFilterSelection | null;
  onSelectedFilterChange: (
    selectedFilter: ReportsFilterSelection | null
  ) => void;
  disableFilter?: boolean;
};

const FILTER_PLACEHOLDER = 'Filter AOR/Incident...';

const getOptionKey = (option: ReportsFilterSelection) =>
  `${option.kind}:${option.value}`;

const ReportsFilterBar: React.FC<ReportsFilterBarProps> = ({
  groupedOptions,
  selectedFilter,
  onSelectedFilterChange,
  disableFilter = false,
}) => {
  const allOptions = useMemo(
    () => groupedOptions.flatMap((group) => group.options),
    [groupedOptions]
  );

  const groups = useMemo<ComboboxGroupedFieldGroup[]>(
    () =>
      groupedOptions.map((group) => ({
        key: group.label,
        label: group.label,
        items: group.options.map((option) => ({
          label: option.label,
          value: getOptionKey(option),
        })),
      })),
    [groupedOptions]
  );

  const handleChange = (value: string | string[] | null) => {
    const key = typeof value === 'string' ? value : null;
    const nextOption = key
      ? allOptions.find((option) => getOptionKey(option) === key)
      : null;
    onSelectedFilterChange(nextOption ?? null);
  };

  return (
    <div className="reports-filter-bar">
      <div className="reports-filter-bar__controls">
        <div className="reports-filter-bar__filter-control">
          <div className="reports-filter-bar__select-wrapper">
            <ComboboxGroupedField
              groups={groups}
              value={selectedFilter ? getOptionKey(selectedFilter) : null}
              onChange={handleChange}
              placeholder={FILTER_PLACEHOLDER}
              disabled={disableFilter}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsFilterBar;
